<template>
  <v-container>
    <v-layout>
      <v-flex xs12 sm8 offset-sm2>
        <div class="account-page">
          <router-link to="/" class="back-link">&larr; Back to Dashboard</router-link>

          <h1>Your account</h1>
          <p class="subtitle">{{ isFaculty ? 'Your name, email, and phone.' : 'How we reach you — and who to call if we ever need to.' }}</p>

          <section class="account-section you-section">
            <h2>That’s you</h2>
            <div class="you-card">
              <p class="you-name">{{ firstName }} {{ lastName }}</p>
              <p v-if="email" class="you-email">
                {{ email }}
                <span class="you-email-note">
                  · to change email, write
                  <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>
                </span>
              </p>
              <div class="you-phone-row">
                <template v-if="!phoneEditing">
                  <span class="you-phone-label">Phone</span>
                  <span class="you-phone-value">
                    <template v-if="phone">{{ phone }}</template>
                    <span v-else class="static-empty">Not set</span>
                  </span>
                  <button type="button" class="edit-link-btn" @click="startPhoneEdit">
                    {{ phone ? 'Edit' : 'Add' }}
                  </button>
                </template>
                <form v-else class="phone-form you-phone-edit" @submit.prevent="submitPhone">
                  <v-text-field
                    v-model="phone"
                    type="tel"
                    placeholder="Mobile number"
                    hide-details
                    dense
                    class="phone-field"
                    autofocus
                  />
                  <div class="edit-actions">
                    <v-btn
                      type="submit"
                      color="primary"
                      dark
                      small
                      :loading="phoneSaving"
                      :disabled="phoneSaving || !phone.trim()"
                    >
                      {{ phoneSaving ? 'Saving…' : 'Save' }}
                    </v-btn>
                    <button type="button" class="edit-link-btn" :disabled="phoneSaving" @click="cancelPhoneEdit">
                      Cancel
                    </button>
                  </div>
                  <p v-if="phoneError" class="form-error" role="alert">{{ phoneError }}</p>
                  <p v-if="phoneSuccess" class="form-success" role="status">{{ phoneSuccess }}</p>
                </form>
              </div>

              <div v-if="!isFaculty" class="you-address-block">
                <template v-if="!addressEditing">
                  <div class="you-phone-row">
                    <span class="you-phone-label">Address</span>
                    <span class="you-phone-value">
                      <template v-if="hasAddress">
                        <span class="address-lines">
                          <span v-for="(line, i) in mailingLines" :key="i">{{ line }}</span>
                        </span>
                      </template>
                      <span v-else class="static-empty">Not set</span>
                    </span>
                    <button type="button" class="edit-link-btn" @click="startAddressEdit">
                      {{ hasAddress ? 'Edit' : 'Add' }}
                    </button>
                  </div>
                </template>
                <form v-else class="address-form" @submit.prevent="submitAddress">
                  <label class="address-select-label" for="profile-country">Country</label>
                  <select
                    id="profile-country"
                    class="address-select"
                    :value="countrySelectValue"
                    @change="onCountryChange($event.target.value)"
                  >
                    <option value="" disabled>Choose a country</option>
                    <option
                      v-for="c in mailingCountrySelectList"
                      :key="c.code"
                      :value="c.code"
                      :disabled="!!c.disabled"
                    >{{ c.name }}</option>
                  </select>
                  <template v-if="structuredMailing">
                    <v-text-field
                      v-model="addressStreet"
                      label="Street address"
                      hide-details
                      dense
                      class="address-field"
                    />
                    <v-text-field
                      v-model="addressStreet2"
                      label="Apt / suite (optional)"
                      hide-details
                      dense
                      class="address-field"
                    />
                    <v-text-field
                      v-model="addressCity"
                      label="City"
                      hide-details
                      dense
                      class="address-field"
                    />
                    <div class="address-row">
                      <v-text-field
                        v-model="addressState"
                        :label="mailingRegionLabel"
                        hide-details
                        dense
                        class="address-field"
                      />
                      <v-text-field
                        v-model="addressZip"
                        :label="mailingPostalLabel"
                        hide-details
                        dense
                        class="address-field"
                      />
                    </div>
                  </template>
                  <v-textarea
                    v-else-if="addressCountry || addressStreet"
                    v-model="addressStreet"
                    label="Mailing address"
                    hint="Write it the way you’d put it on an envelope — including postcode."
                    persistent-hint
                    auto-grow
                    rows="4"
                    hide-details="auto"
                    class="address-field"
                  />
                  <div class="edit-actions">
                    <v-btn
                      type="submit"
                      color="primary"
                      dark
                      small
                      :loading="addressSaving"
                      :disabled="addressSaving"
                    >
                      {{ addressSaving ? 'Saving…' : 'Save' }}
                    </v-btn>
                    <button type="button" class="edit-link-btn" :disabled="addressSaving" @click="cancelAddressEdit">
                      Cancel
                    </button>
                  </div>
                  <p v-if="addressError" class="form-error" role="alert">{{ addressError }}</p>
                  <p v-if="addressSuccess" class="form-success" role="status">{{ addressSuccess }}</p>
                </form>
              </div>
            </div>
          </section>

          <section v-if="!isFaculty" class="account-section">
            <h2>Who should we call?</h2>
            <p class="account-hint" style="margin-bottom: 16px;">
              Someone we can reach during the festival if we need a hand looking after you.
            </p>
            <span v-show="emergencyContactEntered !== true">
              <form @submit.prevent="submitEmergencyContact">
                <p class="form-question">1. Their name</p>
                <v-text-field v-model="emergencyContactName" required />
                <p class="form-question">2. How do you know them?</p>
                <v-text-field
                  v-model="emergencyContactRelationship"
                  placeholder="Mom, partner, roommate…"
                  required
                />
                <p class="form-question">3. Their phone number</p>
                <v-text-field v-model="emergencyContactPhone" number required />
                <p v-if="contactError" class="form-error" role="alert">{{ contactError }}</p>
                <p v-if="contactSuccess" class="form-success" role="status">{{ contactSuccess }}</p>
                <v-btn type="submit" color="primary" dark :loading="contactSaving" :disabled="contactSaving">
                  {{ contactSaving ? 'Saving…' : 'Save' }}
                </v-btn>
              </form>
            </span>
            <span v-show="emergencyContactEntered === true">
              <p>{{ emergencyContactName }} ({{ emergencyContactRelationship }}) <i>+{{ emergencyContactPhone }}</i></p>
              <v-btn color="primary" dark @click="reenterEmergencyContact">Update</v-btn>
            </span>
          </section>

          <section class="account-section related-links">
            <h2>Next stops</h2>
            <ul class="link-list">
              <li><router-link to="/community">Community intros →</router-link></li>
              <li v-if="!isFaculty"><router-link to="/registration">What’s in your package →</router-link></li>
              <li><router-link to="/dietary">How you like to eat →</router-link></li>
              <li><router-link to="/lodging">How you’re getting here →</router-link></li>
              <li v-if="!isFaculty"><router-link to="/billing">Payments →</router-link></li>
            </ul>
          </section>
        </div>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { saveErrorMessage } from '@/utils/save-error'
import {
  MAILING_COUNTRIES,
  mailingCountrySelectList,
  normalizeCountryCode,
  isStructuredMailingCountry,
  regionLabel,
  postalLabel,
  mailingAddressComplete,
  formatMailingLines,
  foldIntoEnvelope
} from '@/config/iabf-mailing-address'

const MAILING_COUNTRY_SELECT_LIST = mailingCountrySelectList()

export default {
  name: 'IABFAccount',
  data () {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      addressStreet: '',
      addressStreet2: '',
      addressCity: '',
      addressState: '',
      addressZip: '',
      addressCountry: '',
      mailingCountrySelectList: MAILING_COUNTRY_SELECT_LIST,
      addressSavedSnapshot: null,
      addressEditing: false,
      addressSaving: false,
      addressError: '',
      addressSuccess: '',
      emergencyContactEntered: false,
      emergencyContactName: '',
      emergencyContactRelationship: '',
      emergencyContactPhone: '',
      contactError: '',
      contactSuccess: '',
      contactSaving: false,
      phoneError: '',
      phoneSuccess: '',
      phoneSaving: false,
      phoneEditing: false,
      phoneSavedSnapshot: ''
    }
  },
  computed: {
    user () {
      return this.$store.getters.user
    },
    currentIABFDancer () {
      return this.$store.getters.currentIABFDancer
    },
    dancerId () {
      return this.currentIABFDancer ? this.currentIABFDancer.id : null
    },
    isFaculty () {
      const d = this.currentIABFDancer || {}
      return !!(d.isFaculty || d.personType === 'faculty')
    },
    hasAddress () {
      return mailingAddressComplete(this.snapshotAddress())
    },
    structuredMailing () {
      if (isStructuredMailingCountry(this.addressCountry)) {
        return true
      }
      if (!this.addressCountry) {
        return !!(this.addressCity || this.addressState || this.addressZip)
      }
      return false
    },
    countrySelectValue () {
      const code = normalizeCountryCode(this.addressCountry)
      if (!code) {
        return ''
      }
      if (MAILING_COUNTRIES.some((c) => c.code === code)) {
        return code
      }
      return 'OTHER'
    },
    mailingRegionLabel () {
      return regionLabel(this.addressCountry)
    },
    mailingPostalLabel () {
      return postalLabel(this.addressCountry)
    },
    mailingLines () {
      return formatMailingLines(this.snapshotAddress())
    },
    contactFormIsValid () {
      return this.emergencyContactName !== '' &&
        this.emergencyContactRelationship !== '' &&
        this.emergencyContactPhone !== ''
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    this.initDancer()
  },
  watch: {
    user (val) {
      if (val) this.initDancer()
    },
    currentIABFDancer (val) {
      if (val && val.id) this.populateFromDancer()
    }
  },
  methods: {
    initDancer () {
      if (this.dancerId) {
        this.populateFromDancer()
        return
      }
      if (!this.user) return
      const uid = this.user.id
      const email = this.user.email
      this.$store.dispatch('loadIABFDancerByUid', uid).then(() => {
        if (this.currentIABFDancer && this.currentIABFDancer.id) return
        if (email) {
          this.$store.dispatch('linkIABFDancerByEmail', { email, uid })
        }
      })
    },
    populateFromDancer () {
      const d = this.currentIABFDancer
      if (!d) return
      this.firstName = d.firstName
      this.lastName = d.lastName
      this.email = d.email
      this.phone = d.phone || ''
      this.phoneSavedSnapshot = this.phone
      // Only open phone editor if they have no number yet
      this.phoneEditing = !this.phone
      this.addressStreet = d.addressStreet || ''
      this.addressStreet2 = d.addressStreet2 || ''
      this.addressCity = d.addressCity || ''
      this.addressState = d.addressState || ''
      this.addressZip = d.addressZip || ''
      this.addressCountry = normalizeCountryCode(d.addressCountry)
      this.addressSavedSnapshot = this.snapshotAddress()
      // Open address editor if incomplete
      this.addressEditing = !this.hasAddress
      this.emergencyContactName = d.emergencyContactName
      this.emergencyContactRelationship = d.emergencyContactRelationship
      this.emergencyContactPhone = d.emergencyContactPhone
      this.emergencyContactEntered = d.emergencyContactEntered || false
    },
    snapshotAddress () {
      return {
        addressStreet: this.addressStreet,
        addressStreet2: this.addressStreet2,
        addressCity: this.addressCity,
        addressState: this.addressState,
        addressZip: this.addressZip,
        addressCountry: this.addressCountry
      }
    },
    applyAddressSnapshot (snap) {
      const s = snap || {}
      this.addressStreet = s.addressStreet || ''
      this.addressStreet2 = s.addressStreet2 || ''
      this.addressCity = s.addressCity || ''
      this.addressState = s.addressState || ''
      this.addressZip = s.addressZip || ''
      this.addressCountry = s.addressCountry || 'US'
    },
    startPhoneEdit () {
      this.phoneEditing = true
      this.phoneError = ''
      this.phoneSuccess = ''
    },
    cancelPhoneEdit () {
      this.phone = this.phoneSavedSnapshot
      this.phoneEditing = !this.phoneSavedSnapshot
      this.phoneError = ''
      this.phoneSuccess = ''
    },
    onCountryChange (code) {
      const next = String(code || '').trim()
      if (!next || next === '_divider') {
        return
      }
      const wasStructured = isStructuredMailingCountry(this.addressCountry)
      const nowStructured = isStructuredMailingCountry(next)
      if (wasStructured && !nowStructured) {
        const folded = foldIntoEnvelope(this.snapshotAddress())
        this.addressStreet = folded.addressStreet
        this.addressStreet2 = folded.addressStreet2
        this.addressCity = folded.addressCity
        this.addressState = folded.addressState
        this.addressZip = folded.addressZip
      }
      this.addressCountry = next
    },
    startAddressEdit () {
      this.addressEditing = true
      this.addressError = ''
      this.addressSuccess = ''
    },
    cancelAddressEdit () {
      this.applyAddressSnapshot(this.addressSavedSnapshot)
      this.addressEditing = !this.hasAddress
      this.addressError = ''
      this.addressSuccess = ''
    },
    reenterEmergencyContact () {
      this.emergencyContactEntered = false
      this.contactError = ''
      this.contactSuccess = ''
    },
    submitPhone () {
      this.phoneError = ''
      this.phoneSuccess = ''
      const phone = String(this.phone || '').trim()
      if (!phone) {
        this.phoneError = 'Add a phone number so we can reach you.'
        return
      }
      if (!this.dancerId) {
        this.phoneError = 'Hang tight — your registration is still loading. Try again in a moment.'
        return
      }
      this.phoneSaving = true
      this.$store.dispatch('updateDancerPhone', {
        id: this.dancerId,
        phone,
        personId: this.currentIABFDancer && this.currentIABFDancer.personId
      }).then(() => {
        this.phone = phone
        this.phoneSavedSnapshot = phone
        this.phoneEditing = false
        this.phoneSuccess = 'Phone number saved.'
        this.phoneSaving = false
      }).catch((err) => {
        this.phoneError = saveErrorMessage(err)
        this.phoneSaving = false
      })
    },
    submitAddress () {
      this.addressError = ''
      this.addressSuccess = ''
      if (!this.dancerId) {
        this.addressError = 'Hang tight — your registration is still loading. Try again in a moment.'
        return
      }
      this.addressSaving = true
      this.$store.dispatch('updateDancerAddress', {
        id: this.dancerId,
        personId: this.currentIABFDancer && this.currentIABFDancer.personId,
        addressStreet: this.addressStreet,
        addressStreet2: this.addressStreet2,
        addressCity: this.addressCity,
        addressState: this.addressState,
        addressZip: this.addressZip,
        addressCountry: this.addressCountry
      }).then((saved) => {
        this.applyAddressSnapshot(saved)
        this.addressSavedSnapshot = this.snapshotAddress()
        this.addressEditing = false
        this.addressSuccess = 'Address saved.'
        this.addressSaving = false
      }).catch((err) => {
        this.addressError = saveErrorMessage(err)
        this.addressSaving = false
      })
    },
    submitEmergencyContact () {
      this.contactError = ''
      this.contactSuccess = ''
      if (!this.contactFormIsValid) {
        if (!this.emergencyContactName) {
          this.contactError = 'Who should we call? Add their name.'
        } else if (!this.emergencyContactRelationship) {
          this.contactError = 'How do you know them? (Mom, partner, friend…)'
        } else {
          this.contactError = 'We’ll need a phone number for them too.'
        }
        return
      }
      if (!this.dancerId) {
        this.contactError = 'Hang tight — your registration is still loading. Try again in a moment.'
        return
      }
      this.contactSaving = true
      this.$store.dispatch('updateEmergencyContact', {
        id: this.dancerId,
        emergencyContactName: this.emergencyContactName,
        emergencyContactRelationship: this.emergencyContactRelationship,
        emergencyContactPhone: this.emergencyContactPhone,
        emergencyContactEntered: true
      }).then(() => {
        this.emergencyContactEntered = true
        this.contactSuccess = 'Got it — thank you!'
        this.contactSaving = false
      }).catch((err) => {
        this.contactError = saveErrorMessage(err)
        this.contactSaving = false
      })
    }
  }
}
</script>

<style scoped>
.account-page {
  max-width: 780px;
  margin: 0 auto;
  padding: 40px 20px 60px;
  color: var(--iabf-ink, #1c1b1f);
}
.back-link {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-faint, #8a8792);
  text-decoration: none;
}
.back-link:hover { color: var(--iabf-ink, #1c1b1f); }
h1 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 36px;
  letter-spacing: -.02em;
  margin: 16px 0 8px;
  color: var(--iabf-ink, #1c1b1f);
}
.subtitle {
  font-size: 17px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin-bottom: 28px;
}
h2 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 26px;
  margin: 0 0 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--iabf-border, #e3e0e8);
  color: var(--iabf-ink, #1c1b1f);
}
.account-section {
  margin-bottom: 36px;
}
.you-card {
  margin-top: 4px;
}
.you-name {
  margin: 0 0 2px;
  font-size: 18px;
  font-weight: 700;
  color: var(--iabf-ink, #1c1b1f);
  line-height: 1.35;
}
.you-email {
  margin: 0 0 10px;
  font-size: 15px;
  color: var(--iabf-ink-muted, #5c5a63);
  line-height: 1.45;
}
.you-email-note {
  font-size: 13px;
  color: var(--iabf-ink-faint, #8a8792);
  font-style: italic;
  font-weight: 400;
}
.you-email-note a {
  font-style: normal;
  font-weight: 600;
}
.you-phone-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px 12px;
  font-size: 15px;
}
.you-phone-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--iabf-ink-faint, #8a8792);
}
.you-phone-value {
  color: var(--iabf-ink, #1c1b1f);
  font-weight: 500;
}
.you-phone-edit {
  flex: 1 1 100%;
  max-width: 320px;
}
.you-address-block {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--iabf-border, #e3e0e8);
}
.address-lines {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.4;
}
.address-form {
  margin-top: 4px;
  max-width: 420px;
}
.address-select-label {
  display: block;
  font-size: 12px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin-bottom: 4px;
}
.address-select {
  width: 100%;
  min-height: 40px;
  padding: 8px 10px;
  margin-bottom: 8px;
  font-size: 16px;
  border: 1px solid var(--iabf-border, #e3e0e8);
  border-radius: 8px;
  background: #fff;
}
.address-field {
  margin-top: 4px;
}
.address-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.phone-form {
  margin-top: 0;
  max-width: 360px;
}
.phone-field {
  margin-top: 0;
}
.static-empty {
  color: var(--iabf-ink-faint, #8a8792);
  font-style: italic;
}
.edit-link-btn {
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--iabf-primary, #8a957d);
  cursor: pointer;
  text-decoration: underline;
}
.edit-link-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.edit-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  margin-top: 8px;
}
.form-question {
  font-weight: 600;
  margin: 0 0 6px;
  font-size: 15px;
}
.form-error {
  color: var(--iabf-danger, #b54a4a);
  font-size: 14px;
  margin: 8px 0 0;
}
.form-success {
  color: var(--iabf-success, #5f7a55);
  font-size: 14px;
  margin: 8px 0 0;
}
.contact-line {
  font-size: 17px;
  line-height: 1.6;
  color: var(--iabf-ink-muted, #5c5a63);
}
.account-hint {
  font-size: 14px;
  color: var(--iabf-ink-faint, #8a8792);
  font-style: italic;
}
.form-question {
  padding-top: 10px;
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 700;
  font-size: 17px;
  color: var(--iabf-ink, #1c1b1f);
  margin-bottom: 8px;
}
.form-error {
  color: var(--iabf-danger, #b54a4a);
  font-size: 15px;
  margin: 12px 0 8px;
}
.form-success {
  color: var(--iabf-success, #5f7a55);
  font-size: 15px;
  margin: 12px 0 8px;
}
.link-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.link-list li {
  margin-bottom: 10px;
}
.link-list a {
  color: var(--iabf-primary, #8a957d);
  font-size: 17px;
  text-decoration: none;
  font-weight: 700;
}
.link-list a:hover { text-decoration: underline; }
p, li {
  font-family: var(--iabf-font-body, inherit);
  font-size: 17px;
  line-height: 1.7;
  color: var(--iabf-ink-muted, #5c5a63);
}
ul { padding-left: 20px; }
a { color: var(--iabf-primary, #8a957d); }
</style>
